import React from 'react';
import {
  Security as CrowdStrikeIcon,
  Shield as SentinelOneIcon,
  Microsoft as MicrosoftIcon,
  Hub as GenericIcon,
  Whatshot as CortexIcon,
  GppGood as SophosIcon,
} from '@mui/icons-material';

/**
 * Vendor-facing metadata shared by the Integrations UI. Everything that can be
 * discovered from the backend (types, credential fields, capabilities) comes
 * from GET /agents/types; this module only holds presentation strings.
 */

export const VENDOR_META = {
  crowdstrike: {
    label: 'CrowdStrike Falcon',
    shortLabel: 'CrowdStrike',
    color: '#e0261e',
    setupHint: 'API client needs scopes Alerts:Read, Hosts:Read/Write and Indicators (Falcon Intelligence):Read.',
    endpointHelp: 'Leave blank for US-1, or use https://api.us-2.crowdstrike.com, https://api.eu-1.crowdstrike.com or https://api.laggar.gcw.crowdstrike.com.',
    endpointPlaceholder: 'https://api.crowdstrike.com',
  },
  sentinelone: {
    label: 'SentinelOne',
    shortLabel: 'SentinelOne',
    color: '#6b3fa0',
    setupHint: 'Needs an API token with Viewer + Threats + Agent actions.',
    endpointHelp: 'Your console URL, e.g. https://usea1-partners.sentinelone.net.',
    endpointPlaceholder: 'https://your-console.sentinelone.net',
  },
  microsoft_defender: {
    label: 'Microsoft Defender for Endpoint',
    shortLabel: 'Microsoft Defender',
    color: '#0078d4',
    setupHint: 'Needs an Entra app with Alert.Read.All, Machine.Read.All, Machine.Isolate, File.Read.All and Ti.ReadWrite.',
    endpointHelp: 'Defaults to https://api.securitycenter.microsoft.com.',
    endpointPlaceholder: 'https://api.securitycenter.microsoft.com',
  },
  cortex_xdr: {
    label: 'Palo Alto Cortex XDR',
    shortLabel: 'Cortex XDR',
    color: '#fa582d',
    setupHint: 'Create a Standard API key (Settings → Configurations → Integrations → API Keys) with the Responder role. Enter the key ID as "API Key ID" and the key as the API token.',
    endpointHelp: 'Your API URL from the API Keys page, e.g. https://api-acme.xdr.us.paloaltonetworks.com.',
    endpointPlaceholder: 'https://api-<tenant>.xdr.<region>.paloaltonetworks.com',
    credentialLabels: { tenantId: 'API Key ID', apiToken: 'API Key (Standard)' },
    requiredOverride: { tenantId: true, apiEndpoint: true },
  },
  sophos_central: {
    label: 'Sophos Central',
    shortLabel: 'Sophos',
    color: '#0a3d91',
    setupHint: 'Create a Service Principal (Global Settings → API Credentials) with the Service Principal Super Admin role. Tenant ID and data region are discovered automatically for tenant credentials.',
    endpointHelp: 'Optional data-region URL (e.g. https://api-us01.central.sophos.com); leave blank to auto-detect.',
    endpointPlaceholder: 'auto-detected',
    credentialLabels: { tenantId: 'Tenant ID (optional, partner keys only)', apiEndpoint: 'Data region URL (optional)' },
    optionalOverride: { tenantId: true, apiEndpoint: true },
  },
};

export const getVendorLabel = (type, types = []) => {
  const fromServer = types.find((t) => t.type === type);
  return (fromServer && fromServer.label) || (VENDOR_META[type] && VENDOR_META[type].label) || type || 'Unknown';
};

export const getVendorIcon = (type, props = {}) => {
  switch (type) {
    case 'crowdstrike':
      return <CrowdStrikeIcon {...props} />;
    case 'sentinelone':
      return <SentinelOneIcon {...props} />;
    case 'microsoft_defender':
      return <MicrosoftIcon {...props} />;
    case 'cortex_xdr':
      return <CortexIcon {...props} />;
    case 'sophos_central':
      return <SophosIcon {...props} />;
    default:
      return <GenericIcon {...props} />;
  }
};

export const CREDENTIAL_FIELD_META = {
  clientId: { label: 'Client ID', password: false, maskedKey: 'hasClientId' },
  clientSecret: { label: 'Client Secret', password: true, maskedKey: 'hasClientSecret' },
  tenantId: { label: 'Tenant ID', password: false, maskedKey: 'hasTenantId' },
  apiToken: { label: 'API Token', password: true, maskedKey: 'hasApiToken' },
  memberCid: { label: 'Member CID (MSSP, optional)', password: false, optional: true },
  apiEndpoint: { label: 'API endpoint / cloud', password: false, optional: true },
};

export const CAPABILITY_LABELS = {
  ingestion: 'Alert ingestion',
  endpoints: 'Endpoints',
  isolation: 'Isolation',
  threatIntel: 'Threat intel',
  iocManagement: 'IOC management',
  alertStatus: 'Alert status sync',
};

export const SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

export const INDICATOR_TYPES = ['IP Address', 'Domain', 'URL', 'File Hash', 'Email'];

/** Client-side success rate from status.stats (the API also sends successRate, but it can be stale). */
export const computeSuccessRate = (agent) => {
  const stats = (agent && agent.status && agent.status.stats) || {};
  const total = stats.totalEnrichments || 0;
  if (!total) return null;
  return Math.round(((stats.successfulEnrichments || 0) / total) * 100);
};

/** Extract a readable message (and per-field validation errors) from an axios error. */
export const describeApiError = (err, fallback = 'Request failed') => {
  const data = err && err.response && err.response.data;
  if (!data) return { message: (err && err.message) || fallback, fieldErrors: {} };
  const fieldErrors = {};
  if (Array.isArray(data.errors)) {
    data.errors.forEach((e) => {
      const key = e.path || e.param;
      if (key && !fieldErrors[key]) fieldErrors[key] = e.msg || e.message;
    });
  }
  const detail = Object.values(fieldErrors).join('; ');
  const message = data.message ? (detail ? `${data.message}: ${detail}` : data.message) : data.error || detail || fallback;
  return { message, fieldErrors };
};

export const capabilitiesForType = (type, types = []) => {
  const entry = types.find((t) => t.type === type);
  return (entry && entry.capabilities) || {};
};
